import { Sentry } from '@/sentry'
import { CLIENT_NAME, CLIENT_VERSION, getTabId } from '@/api/clientInfo'
import { AUTH_PERSIST_KEY } from '@/utils/constants'

/**
 * Диагностика разлогинов: почему клиент сам сбросил сессию и что он знал о
 * сессии перед входом. Сервер видит только свою половину (отказ обмена или
 * запроса), решение «выйти» принимает клиент — эти данные уходят в журнал
 * входов и выходов API и в Sentry.
 */

/** Формат причины и prior_state в API: `^[A-Za-z0-9_:.-]{1,64}$` */
const CODE_PATTERN = /^[A-Za-z0-9_:.-]{1,64}$/
const CODE_MAX_LENGTH = 64
const LAST_LOGOUT_KEY = 'easya_last_logout'
const FORCED_PREFIX = 'forced:'

type BreadcrumbLevel = 'info' | 'warning' | 'error'

/** Привести причину к формату API: лишние символы — в `_`, не длиннее 64 */
export function toAuthCode(raw: string): string {
  const code = raw.replace(/[^A-Za-z0-9_:.-]/g, '_').slice(0, CODE_MAX_LENGTH)

  return code !== '' ? code : 'unknown'
}

/** Хлебная крошка Sentry о сессии: начало, успех и сбой обмена токена */
export function authBreadcrumb(
  message: string,
  level: BreadcrumbLevel = 'info',
  data?: Record<string, unknown>,
): void {
  Sentry.addBreadcrumb({ category: 'auth', message, level, data })
}

function storageGet(key: string): string | null {
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

function storageSet(key: string, value: string): void {
  try {
    localStorage.setItem(key, value)
  } catch {
    // хранилище недоступно — prior_state при входе будет no_storage
  }
}

/** Пользователь сам нажал «Выйти» — следующий вход покажет prior_state=manual */
export function rememberManualLogout(): void {
  storageSet(LAST_LOGOUT_KEY, 'manual')
}

/** Сессию сбросил клиент — следующий вход покажет prior_state=forced:<reason> */
export function rememberForcedLogout(reason: string): void {
  storageSet(LAST_LOGOUT_KEY, toAuthCode(`${FORCED_PREFIX}${reason}`))
}

/** Сессия получена (вход, регистрация, активация) — прежняя причина выхода больше не нужна */
export function clearLastLogout(): void {
  try {
    localStorage.removeItem(LAST_LOGOUT_KEY)
  } catch {
    // см. storageSet
  }
}

/**
 * Что клиент знал о сессии перед входом (поле `prior_state` входа):
 * `forced:<причина>` или `manual` — как закончилась прошлая сессия;
 * `no_storage` — следов сессии нет (очистка данных сайта, инкогнито, другой браузер);
 * `storage_unauth` — в хранилище сессия без токена;
 * `storage_auth` — в хранилище живая сессия (вход поверх неё).
 */
export function readPriorState(): string {
  const lastLogout = storageGet(LAST_LOGOUT_KEY)
  if (lastLogout !== null && CODE_PATTERN.test(lastLogout)) {
    return lastLogout
  }

  const persisted = storageGet(AUTH_PERSIST_KEY)
  if (persisted === null) {
    return 'no_storage'
  }

  try {
    const parsed = JSON.parse(persisted) as { state?: { isAuthenticated?: unknown; token?: unknown } }
    const state = parsed.state

    return state?.isAuthenticated === true && typeof state.token === 'string' && state.token !== ''
      ? 'storage_auth'
      : 'storage_unauth'
  } catch {
    return 'storage_unauth'
  }
}

interface ForcedLogoutReport {
  /** Полный адрес `POST /auth/client-events` */
  url: string
  reason: string
  /** Токен сессии: по его подписи сервер найдёт пользователя; в журнал не пишется */
  token: string | null
  userId: number | null
}

/**
 * Сообщить причину принудительного выхода — до того, как токен сотрут.
 *
 * `sendBeacon` переживает уход со страницы (сразу после выхода — редирект на
 * вход), но не ставит заголовки: токен и сведения о клиенте идут полями формы,
 * а форма не требует CORS preflight. Нет sendBeacon или он отказал — fetch с
 * keepalive. Ответ не нужен: это диагностика, а не часть выхода.
 */
export function reportForcedLogout({ url, reason, token, userId }: ForcedLogoutReport): void {
  const code = toAuthCode(reason)
  const body = new URLSearchParams({
    event: 'client_forced_logout',
    reason: code,
    client: CLIENT_NAME,
    client_version: CLIENT_VERSION,
    tab_id: getTabId(),
  })
  if (token) {
    body.set('token', token)
  }

  let queued = false
  try {
    queued = typeof navigator.sendBeacon === 'function' && navigator.sendBeacon(url, body)
  } catch {
    queued = false
  }
  if (!queued) {
    try {
      void fetch(url, { method: 'POST', body, keepalive: true, mode: 'no-cors', credentials: 'omit' }).catch(
        () => undefined,
      )
    } catch {
      // сеть недоступна — останется событие в Sentry
    }
  }

  authBreadcrumb('forced_logout', 'warning', { reason: code })
  Sentry.captureMessage('auth.forced_logout', {
    level: 'info',
    fingerprint: ['auth-forced-logout', code],
    tags: { auth_reason: code, client_tab: getTabId() },
    extra: { user_id: userId, beacon_queued: queued },
  })
}

/** В Sentry — только id пользователя, без почты и имени */
export function setSentryUser(userId: number | null): void {
  Sentry.setUser(userId !== null ? { id: String(userId) } : null)
}
